/**
 * Analytics, without a cookie banner: PostHog (EU cloud, Frankfurt) in
 * cookieless mode. Nothing is stored on the device: PostHog counts visitors
 * with a hash of IP address and browser whose salt changes every day, so a
 * visitor is recognised for one day at most and never across days. Nobody is
 * identified (person_profiles "never"); the account steps come from the
 * database as server events, keyed by case number (Linear MIG-38). It records
 * pageviews, the funnel events below, clicks (autocapture) and heatmaps. No
 * session recording.
 *
 * Everything goes through /relay on migrada.eu (functions/relay), a
 * Cloudflare Pages Function, so ad blockers do not drop it. The relay passes
 * the IP on for the hash only (PostHog discards IPs) and adds the visitor's
 * country, which PostHog cannot look up in cookieless mode. That needs plain
 * JSON, hence disable_compression.
 *
 * Microsoft Clarity was removed on 2026-10-01: Microsoft uses Clarity data for
 * its own purposes, including advertising, which cannot run without consent.
 *
 * Legal basis is legitimate interest (Art. 6(1)(f) GDPR), so visitors can
 * object: the cookie page sets OPT_OUT_KEY and Global Privacy Control is
 * honoured. URLs are sent without query string or fragment, so sign-in codes,
 * reset links and verification tokens never reach PostHog. The cookie and
 * privacy pages describe exactly this; change them together.
 */

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
/** First-party path of the PostHog relay (frontend/functions/relay). */
const RELAY_PATH = "/relay";

export const OPT_OUT_KEY = "migrada_analytics_off";

/** Funnel and product events. Never put an email, a name or free text in props. */
export type AnalyticsEvent =
  | "cta_start_application"
  | "plan_form_started"
  | "plan_step_completed"
  | "plan_form_submitted"
  | "plan_form_failed"
  | "plan_country_suggested"
  | "plan_reveal_shown"
  | "plan_save_case_clicked"
  | "signup_opened"
  | "signup_completed"
  | "signin_completed"
  | "community_join_clicked"
  | "community_question_sent"
  | "buddy_application_sent"
  | "contact_sent"
  | "university_enquiry_sent"
  // The app: the activation funnel (plan request -> account -> case -> upload -> stages).
  | "case_opened"
  | "plan_requested"
  | "document_uploaded"
  | "date_added"
  | "stage_completed"
  | "milestone_shared"
  | "helpful_marked"
  | "checklist_printed"
  | "referral_shared"
  | "private_question_sent"
  | "parents_shared"
  | "step_parked"
  | "step_planned"
  | "step_waiting"
  | "step_still_waiting"
  | "step_arrived"
  | "calendar_added"
  | "plan_checked"
  | "timeline_sent"
  | "university_picked"
  | "ru_check_started"
  | "ru_check_completed"
  | "position_set"
  | "guide_skipped"
  | "guide_finished";

type Props = Record<string, string | number | boolean | null | undefined>;

let started = false;

function isProductionDomain() {
  const host = window.location.hostname;
  return host === "migrada.eu" || host === "www.migrada.eu";
}

export function isOptedOut(): boolean {
  try {
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return true;
    return window.localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

function enabled() {
  return typeof window !== "undefined" && isProductionDomain() && !isOptedOut();
}

/** "https://migrada.eu/auth/callback?code=…#access_token=…" -> "https://migrada.eu/auth/callback" */
function stripUrl(value: unknown): unknown {
  if (typeof value !== "string" || !value) return value;
  try {
    const u = new URL(value);
    return u.origin + u.pathname;
  } catch {
    return value.split(/[?#]/)[0];
  }
}

const URL_RE = /^https?:\/\//i;

/**
 * Every web address in an event, as a value or as a key (heatmaps are keyed by
 * URL), at any depth: $current_url, $referrer, $session_entry_url, $set_once
 * and so on. New properties PostHog adds later are covered too.
 */
function scrub(value: unknown, depth = 0): unknown {
  if (depth > 6) return value;
  if (typeof value === "string") return URL_RE.test(value) ? stripUrl(value) : value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[URL_RE.test(k) ? String(stripUrl(k)) : k] = scrub(v, depth + 1);
    }
    return out;
  }
  return value;
}

function sanitize<T extends Record<string, unknown> | null>(event: T): T {
  if (!event) return event;
  for (const key of ["properties", "$set", "$set_once"]) {
    if (event[key]) (event as Record<string, unknown>)[key] = scrub(event[key]);
  }
  return event;
}

/** Starts PostHog once. Safe to call more than once. */
/**
 * Cloudflare Web Analytics: page views and visits, counted without cookies or
 * anything stored on the device and without identifying anyone. The token is
 * public (it is in every page that uses it). It follows the same switch as
 * PostHog: production domain only, never with GPC or the opt-out.
 */
const CF_BEACON_TOKEN = "d3e4b5a613174e9bb7fc2c889ac28859";

function loadCloudflareBeacon() {
  if (document.querySelector("script[data-cf-beacon]")) return;
  const s = document.createElement("script");
  s.defer = true;
  s.src = "https://static.cloudflareinsights.com/beacon.min.js";
  s.setAttribute("data-cf-beacon", JSON.stringify({ token: CF_BEACON_TOKEN, spa: true }));
  document.head.appendChild(s);
}

export function initAnalytics() {
  if (started || !enabled()) return;
  started = true;
  try {
    loadCloudflareBeacon();
  } catch {
    // Analytics must never break the site.
  }
  try {
    // Immediately, not on idle: the PostHog stub only creates capture() inside
    // init(), so anything tracked earlier would be lost. init() loads its script async.
    const ph = (window as any).posthog;
    if (!POSTHOG_KEY || !ph || typeof ph.init !== "function") return;
    ph.init(POSTHOG_KEY, {
      api_host: window.location.origin + RELAY_PATH,
      ui_host: "https://eu.posthog.com",
      cookieless_mode: "always",
      person_profiles: "never",
      disable_compression: true,
      capture_pageview: false, // sent by trackPageview on every route change
      capture_pageleave: true,
      autocapture: true,
      enable_heatmaps: true,
      disable_session_recording: true,
      before_send: sanitize,
    });
  } catch {
    // Analytics must never break the site.
  }
}

export function track(event: AnalyticsEvent, props?: Props) {
  if (!enabled()) return;
  try {
    (window as any).posthog?.capture?.(event, props);
  } catch {
    // ignore
  }
}

export function trackPageview(path: string) {
  if (!enabled()) return;
  try {
    (window as any).posthog?.capture?.("$pageview", { $current_url: window.location.origin + path });
  } catch {
    // ignore
  }
}

/** Visitor objection (Art. 21 GDPR). Takes effect on the next page load. */
export function setOptOut(off: boolean) {
  try {
    if (off) window.localStorage.setItem(OPT_OUT_KEY, "1");
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // ignore
  }
}
